import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box,
  Button,
  Link,
  TableContainer,
  Typography,
} from '@mui/material';

import type { KnowledgeHubAgentSource, KnowledgeHubHybridSearchResult } from './types';

const SOURCE_LINK_PREFIX = 'athena-source:';

export function stripInlineSourceMarkers(markdown: string): string {
  return String(markdown || '')
    .replace(/\s*\[(\d+)\](?=(?:[.,;:!?])?(\s|$))/g, '')
    .replace(/[ \t]{2,}/g, ' ');
}

function withSourceLinks(markdown: string): string {
  return String(markdown || '').replace(/\[(\d+)\]/g, (_match, referenceNumber) => (
    `[[${referenceNumber}]](${SOURCE_LINK_PREFIX}${referenceNumber})`
  ));
}

function safeHttpUrl(value: unknown): string {
  const href = String(value || '').trim();
  if (!/^https?:\/\//i.test(href)) return '';
  return href;
}

export interface KnowledgeHubAnswerContentProps {
  markdown: string;
  sources?: KnowledgeHubAgentSource[];
  results?: KnowledgeHubHybridSearchResult[];
  onSourceClick?: (referenceNumber: number) => void;
  compact?: boolean;
  showSourceMarkers?: boolean;
}

const KnowledgeHubAnswerContent: React.FC<KnowledgeHubAnswerContentProps> = ({
  markdown,
  sources = [],
  results = [],
  onSourceClick,
  compact = false,
  showSourceMarkers = false,
}) => {
  const availableReferences = new Set([
    ...sources.map((source) => source.referenceNumber),
    ...results.map((_result, index) => index + 1),
  ]);

  return (
    <Box
      sx={{
        color: '#1f2a37',
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          h1: ({ children }) => (
            <Typography variant={compact ? 'h6' : 'h5'} sx={{ fontWeight: 850, mt: 1.5, mb: 1 }}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="h6" sx={{ fontWeight: 850, mt: 1.5, mb: 0.75 }}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="subtitle1" sx={{ fontWeight: 850, mt: 1.25, mb: 0.5 }}>
              {children}
            </Typography>
          ),
          p: ({ children }) => (
            <Typography variant="body1" sx={{ lineHeight: 1.72, mb: 1.1 }}>
              {children}
            </Typography>
          ),
          ul: ({ children }) => (
            <Box component="ul" sx={{ pl: 3, my: 0.75, '& li': { mb: 0.35, lineHeight: 1.65 } }}>
              {children}
            </Box>
          ),
          ol: ({ children }) => (
            <Box component="ol" sx={{ pl: 3, my: 0.75, '& li': { mb: 0.35, lineHeight: 1.65 } }}>
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <Typography component="li" variant="body1" sx={{ display: 'list-item' }}>
              {children}
            </Typography>
          ),
          table: ({ children }) => (
            <TableContainer
              sx={{
                my: 1.25,
                border: '1px solid #d9e2ec',
                borderRadius: 2,
                overflowX: 'auto',
                bgcolor: '#fff',
                boxShadow: '0 8px 22px rgba(7,47,83,0.05)',
              }}
            >
              <Box
                component="table"
                sx={{
                  minWidth: 520,
                  width: '100%',
                  borderCollapse: 'collapse',
                  borderSpacing: 0,
                  '& th, & td': {
                    px: 0.85,
                    py: 0.25,
                    borderBottom: '1px solid #e4ebf3',
                    fontSize: '0.84rem',
                    lineHeight: 1.18,
                    height: 26,
                    verticalAlign: 'middle',
                    color: '#263241',
                  },
                  '& th': {
                    bgcolor: '#f3f8fe',
                    color: '#1f2a37',
                    fontWeight: 850,
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                    fontSize: '0.8rem',
                    letterSpacing: '-0.035em',
                    lineHeight: 1.1,
                  },
                  '& th:last-of-type, & td:last-of-type': {
                    minWidth: 150,
                  },
                  '& td:last-of-type': {
                    whiteSpace: 'normal',
                  },
                  '& tbody tr:nth-of-type(even)': {
                    bgcolor: '#fbfdff',
                  },
                  '& tbody tr:hover': {
                    bgcolor: '#f6faff',
                  },
                  '& p, & .MuiTypography-root': {
                    margin: '0 !important',
                    lineHeight: '1.18 !important',
                    fontSize: '0.84rem',
                    display: 'inline',
                  },
                }}
              >
                {children}
              </Box>
            </TableContainer>
          ),
          thead: ({ children }) => <Box component="thead">{children}</Box>,
          tbody: ({ children }) => <Box component="tbody">{children}</Box>,
          tr: ({ children }) => <Box component="tr">{children}</Box>,
          th: ({ children }) => (
            <Box
              component="th"
              scope="col"
            >
              {children}
            </Box>
          ),
          td: ({ children }) => (
            <Box
              component="td"
            >
              {children}
            </Box>
          ),
          a: ({ href, children }) => {
            const rawHref = String(href || '');
            if (rawHref.startsWith(SOURCE_LINK_PREFIX)) {
              const referenceNumber = Number(rawHref.slice(SOURCE_LINK_PREFIX.length));
              const enabled = availableReferences.has(referenceNumber);
              return (
                <Button
                  size="small"
                  variant="text"
                  disabled={!enabled}
                  onClick={() => onSourceClick?.(referenceNumber)}
                  sx={{
                    minWidth: 0,
                    p: 0,
                    mx: 0.2,
                    fontWeight: 850,
                    textTransform: 'none',
                    verticalAlign: 'baseline',
                    lineHeight: 'inherit',
                  }}
                >
                  {children}
                </Button>
              );
            }
            const safeHref = safeHttpUrl(rawHref);
            if (!safeHref) return <>{children}</>;
            return (
              <Link href={safeHref} target="_blank" rel="noopener noreferrer">
                {children}
              </Link>
            );
          },
          img: () => null,
        }}
      >
        {showSourceMarkers ? withSourceLinks(markdown) : stripInlineSourceMarkers(markdown)}
      </ReactMarkdown>
    </Box>
  );
};

export default KnowledgeHubAnswerContent;
